import { toObject } from '@m1/geo-utils';
import OlMap from '@m1/ol-map';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import { toLonLat } from 'ol/proj';
import React, { useMemo, useState } from 'react';
import { MdOutlineCheck, MdOutlineClose } from 'react-icons/md';
import { RiArrowDownSLine, RiArrowUpSLine, RiMoreLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';

import styles from './Frame.module.scss';
import Options from './Options';

import actions from '@/actions';
import ColorPickerModal from '@/components/modals/ColorPicker';
import Bar from '@/components/ui/Bar';
import ConfirmSlider from '@/components/ui/ConfirmSlider';
import { FunctionButton } from '@/components/ui/kit/Button';
import RobotThumbnail from '@/components/ui/RobotThumbnail';
import { ROBOT_STATUS } from '@/define/robotStatus';
import useWaitingRobot from '@/hooks/useWaitingRobot';
import { modalManager } from '@/libs/ModalContainer';
import telemetryStore from '@/stores/telemetryStore';

const cx = classNames.bind(styles);

const Frame = ({ robot, mode, top, children }) => {
  const dispatch = useDispatch();
  const targetRobotIds = useSelector((state) => state.robot.targetRobotIds);
  const robotStatus = useSelector((state) => state.robotStatus[robot.id]);
  const [monitorStep, setMonitorStep] = useState(0);
  const [isShowOptions, setShowOptions] = useState(false);
  const [isShowConfirm, setShowConfirm] = useState(false);
  const isWaiting = useWaitingRobot(robot.id);

  // 이상 여부
  const hasIssue = useMemo(() => robotStatus?.code === ROBOT_STATUS.ISSUE.code && !isWaiting, [robotStatus, isWaiting]);
  // 선택 여부
  const isTarget = useMemo(() => targetRobotIds.includes(robot.id), [targetRobotIds]);

  const monitors = useMemo(() => {
    return Array.isArray(children) ? children : [children];
  }, []);

  useUpdateEffect(() => {
    // 로봇 연결 해제된 경우
    if (!robot.isActive) {
      setMonitorStep(0);
    }
  }, [robot.isActive]);

  useUpdateEffect(() => {
    if (isTarget && isWaiting) {
      dispatch(actions.command.resetActivePanels());
      dispatch(actions.robot.toggle(robot.id));
    }
    if (!isWaiting) {
      setShowConfirm(false);
    }
  }, [isWaiting]);

  const showColorPicker = () => {
    modalManager.show(ColorPickerModal, { robot });
  };

  const handleClick = (e) => {
    // 수정자 키 조합 여부
    // - ctrlKey: Windows/Linux의 Ctrl 키
    // - metaKey: macOS의 Cmd 키 (⌘)
    const isModifierPressed = e.ctrlKey || e.metaKey;

    // 수정자 키 조합 없이 단일 선택 시
    if (!isModifierPressed) {
      dispatch(actions.robot.reset());
    }

    dispatch(actions.command.resetActivePanels());
    dispatch(actions.robot.toggle(robot.id));

    // 단일 선택 시, 또는 처음 선택 시
    if (!isModifierPressed || !targetRobotIds.includes(robot.id)) {
      const overlay = OlMap.getMap().getOverlayById(`robots/${robot.id}`);
      if (!overlay) return;

      const position = overlay.getPosition();
      OlMap.setCenter(toObject(toLonLat(position)), { duration: 300 });
    }
  };

  const decreaseBodyStep = () => {
    if (isWaiting || hasIssue) {
      setMonitorStep(0);
    } else if (0 < monitorStep) {
      setMonitorStep(monitorStep - 1);
    }
  };

  const increaseBodyStep = () => {
    if (isWaiting || hasIssue) {
      setMonitorStep(monitors.length);
    } else if (monitorStep < monitors.length) {
      setMonitorStep(monitorStep + 1);
    }
  };

  const toggleOptions = () => {
    setShowOptions(!isShowOptions);
  };

  const toggleConfirm = () => {
    setShowConfirm(!isShowConfirm);
  };

  const doDisconnect = () => {
    dispatch(actions.robots.deactivate(robot.id));
    dispatch(actions.address.remove(robot.id));
    dispatch(actions.robotOptions.remove(robot.id));
    dispatch(actions.robotParams.remove(robot.id));
    dispatch(actions.robotStatus.remove(robot.id));
    telemetryStore.remove(robot.id);
    dispatch(actions.missions.remove(robot.id));
    dispatch(actions.notifications.remove(robot.id));
  };

  return (
    <div id={`dashboard/${robot.id}`} className={cx('container')}>
      <div className={cx(['tab', { target: isTarget }])}>
        <div>
          <MdOutlineCheck size={20} color="white" />
        </div>
      </div>
      <div className={cx(['box', { inactive: !robot.isActive }])}>
        <div
          className={cx(['color', { clickable: robot.isOwned }])}
          style={{ backgroundColor: robot.color }}
          onClick={showColorPicker}
        />
        {/* Top */}
        {robot.isActive && top}
        {/* Face */}
        <div className={cx(['face', { clickable: !isWaiting && robot.isActive }])} onClick={handleClick}>
          <RobotThumbnail
            robot={robot}
            className={cx(['thumbnail', { active: robot.isActive }])}
            style={{ backgroundColor: robot.color }}
          />
          <div className={cx('info')}>
            <div className={cx(['name', { active: robot.isActive }])}>{robot.name}</div>
            {robot.isActive && (
              <div className={cx('mode')}>
                {isWaiting && (
                  <div className={cx('waiting')}>
                    Waiting<span className={cx('dots')}></span>
                  </div>
                )}
                {!isWaiting && mode}
              </div>
            )}
          </div>
        </div>
        {/* Options */}
        {isShowOptions && <Options robot={robot} />}
        {!isShowOptions && (
          <>
            {/* Monitors */}
            {monitors.map((section, index) => {
              const hide = !robot.isActive || isWaiting || hasIssue || monitorStep < index + 1;

              return (
                <div key={index} className={cx(['monitor', { hide }])}>
                  {section}
                </div>
              );
            })}
            {/* Waiting */}
            {isWaiting && 0 < monitorStep && (
              <div className={cx('messageWrapper')}>
                <div className={cx('title')}>Communication Lost</div>
                <div className={cx('message')}>
                  Please wait for the reconnection,
                  <br />
                  or manually disconnect the robot.
                </div>
                {!isShowConfirm && (
                  <FunctionButton text="Disconnect" onClick={toggleConfirm} className={cx('button')} />
                )}
                {isShowConfirm && (
                  <div className={cx('sliderWrapper')}>
                    <ConfirmSlider width={172} height={40} onConfirm={doDisconnect} labelStyle={{ fontSize: 12 }} />
                    <div className={cx('cancel')} onClick={toggleConfirm}>
                      <MdOutlineClose size={16} color="white" />
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Issue */}
            {hasIssue && 0 < monitorStep && (
              <div className={cx('messageWrapper')}>
                <div className={cx('title')}>[{robotStatus.issueCode}] Error Occurred</div>
                <div className={cx('message')}>
                  Please reboot the robot,
                  <br />
                  or contact support for assistance.
                </div>
              </div>
            )}
          </>
        )}
        {robot.isActive && (
          <div className={cx('bottom')}>
            <div
              className={cx(['button', { clickable: 0 < monitorStep && !isShowOptions }])}
              onClick={decreaseBodyStep}>
              <RiArrowUpSLine size={14} color={0 < monitorStep && !isShowOptions ? 'white' : 'gray'} />
            </div>
            <Bar size={6} />
            <div
              className={cx(['button', { clickable: monitorStep < monitors.length && !isShowOptions }])}
              onClick={increaseBodyStep}>
              <RiArrowDownSLine size={14} color={monitorStep < monitors.length && !isShowOptions ? 'white' : 'gray'} />
            </div>
          </div>
        )}
        {robot.isActive && (
          <div className={cx(['more', { active: isShowOptions }])} onClick={toggleOptions}>
            <RiMoreLine size={14} color="white" />
          </div>
        )}
        <div className={cx('idCopy')} onClick={() => navigator.clipboard.writeText(robot.id)} />
      </div>
    </div>
  );
};

export default Frame;
